import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_LANG,
  LANGS,
  errorMessage,
  myIdFailureMessage,
  translate,
  type Lang,
} from '@asko/shared';
import { ApiError, setApiLang } from './api';

/**
 * Ikki tilli interfeys: o'zbekcha va ruscha.
 *
 * Sotuvchi o'ziga qulay tilni tanlaydi, tanlov brauzerda saqlanadi.
 * Standart til — o'zbekcha.
 *
 * Bu yerda faqat interfeys matnlari. Backend xabarlari (validatsiya, xato
 * kodlari, MyID natijalari) `@asko/shared/i18n` lug'atidan olinadi — u
 * ikkala tomon uchun umumiy, shuning uchun matn ikki joyda takrorlanmaydi.
 */

export { LANGS, type Lang };

const STORAGE_KEY = 'asko.lang';

const uz = {
  'app.name': 'ASKO broker',
  'app.product': 'Nasiya',

  'lang.uz': "O'zbekcha",
  'lang.ru': 'Ruscha',
  'lang.aria': 'Interfeys tili',

  'common.cancel': 'Bekor qilish',
  'common.close': 'Yopish',
  'common.loading': 'Yuklanmoqda',

  'theme.aria': 'Mavzu',
  'theme.system': 'Tizim sozlamasi',
  'theme.light': "Yorug'",
  'theme.dark': "Qorong'i",
  'nav.users': 'Foydalanuvchilar',
  'users.alifReady': 'Yuboradi (ID: {id})',
  'users.alifSystem': 'Umumiy ID bilan',
  'users.alifNone': "Alifga yubora olmaydi",
  'flow.onBehalf': 'Kim nomidan yuborilsin',
  'flow.onBehalfHint': "Alifga so'rov shu sotuvchi nomidan ketadi.",
  'flow.onBehalfNone': "Alifga yuborishi mumkin bo'lgan sotuvchi yo'q",
  'stores.title': "Do'konlar",
  'stores.hint': "O'z hisobimiz uchun: kim qayerda ishlaydi. Provayderlarga yuborilmaydi.",
  'stores.name': "Do'kon nomi",
  'stores.namePlaceholder': "Masalan: ASKO Chilonzor",
  'stores.add': "Qo'shish",
  'stores.created': "Do'kon qo'shildi",
  'stores.sellers': '{n} sotuvchi',
  'event.details': 'Tafsilotlar',
  'event.hide': 'Yopish',
  'event.identification.start': "Identifikatsiya boshlandi",
  'event.identification.handoff_created': 'QR havolasi yaratildi',
  'event.identification.success': 'Identifikatsiya o\'tdi',
  'event.identification.failed': "Identifikatsiya o'tmadi",
  'event.identification.reused': "Takroriy mijoz — MyID so'ralmadi",
  'event.identification.cancelled': 'Identifikatsiya bekor qilindi',
  'event.scoring_request.start': "Scoring boshlandi",
  'event.scoring_request.choose_provider': 'Provayder tanlandi',
  'event.provider_result.scoring_approved': 'Provayder tasdiqladi',
  'event.provider_result.scoring_rejected': 'Provayder rad etdi',
  'event.provider_result.scoring_error': 'Provayder xato qaytardi',
  'event.provider_result.scoring_timeout': 'Provayder javob bermadi',
  'event.provider_result.scoring_awaiting_otp': "Provayder kod so'radi",
  'event.provider_result.scoring_otp_confirmed': 'Kod tasdiqlandi',
  'event.provider_result.scoring_otp_failed': "Kod noto'g'ri kiritildi",
  'event.provider_result.scoring_otp_resent': 'Kod qayta yuborildi',
  'event.otp.sent': 'SMS kod yuborildi',
  'event.otp.verified': 'Telefon tasdiqlandi',
  'event.sales_user.login': 'Tizimga kirdi',
  'event.sales_user.login_failed': "Kirish urinishi muvaffaqiyatsiz",
  'event.sales_user.created': 'Foydalanuvchi yaratildi',
  'event.sales_user.updated': "Foydalanuvchi o'zgartirildi",
  'event.sales_user.password_set': "Parol o'rnatildi",
  'event.sales_user.password_changed': 'Parol almashtirildi',
  'event.store.created': "Do'kon yaratildi",
  'events.title': 'Nima bo\'ldi',
  'events.empty': "Hodisa yo'q",
  'events.userTitle': '{name} — oxirgi amallar',
  'events.clientTitle': '{name} — barcha urinishlar',
  'users.title': 'Foydalanuvchilar',
  'users.hint': "Sotuvchilar, ularning do'koni va Alifdagi raqami.",
  'users.add': "+ Foydalanuvchi qo'shish",
  'users.create': 'Yaratish',
  'users.creating': 'Yaratilmoqda…',
  'users.created': 'Foydalanuvchi yaratildi',
  'users.name': 'Ism',
  'users.phone': 'Telefon',
  'users.password': 'Parol',
  'users.passwordHint': "Kamida 8 belgi",
  'users.role': 'Roli',
  'users.store': "Do'kon",
  'users.noStore': "Do'konsiz",
  'users.alifId': 'Alif ID',
  'users.alifIdHint': 'Alifdagi user-id',
  'users.myidToday': 'MyID bugun',
  'users.lastLogin': 'Oxirgi kirish',
  'users.disabled': "O'chirilgan",
  'users.disable': "O'chirish",
  'users.enable': 'Yoqish',
  'users.disableTitle': "Foydalanuvchini o'chirish",
  'users.disableBody': "{name} endi tizimga kira olmaydi. Ma'lumotlari saqlanadi.",
  'users.setPassword': 'Parol',
  'users.setPasswordFor': '{name} uchun yangi parol',
  'users.newPassword': 'Yangi parol',
  'users.passwordSet': "Parol o'rnatildi",
  'users.save': 'Saqlash',
  'role.SALES': 'Sotuvchi',
  'role.ADMIN': "Ma'mur",
  'password.title': 'Parolni almashtirish',
  'password.current': 'Joriy parol',
  'password.next': 'Yangi parol',
  'password.repeat': 'Yangi parolni takrorlang',
  'password.mismatch': 'Parollar mos kelmadi',
  'password.save': 'Almashtirish',
  'password.done': 'Parol almashtirildi',
  'nav.flow': 'Yangi ariza',
  'nav.history': 'Tarix',
  'header.logout': 'Chiqish',
  'logout.title': 'Tizimdan chiqasizmi?',
  'logout.body': "To'ldirilayotgan anketa saqlanmaydi.",
  'logout.confirm': 'Ha, chiqish',

  'login.subtitle': 'Sotuvchi kabineti',
  'login.phone': 'Telefon raqami',
  'login.password': 'Parol',
  'login.submit': 'Kirish',
  'login.submitting': 'Tekshirilmoqda…',

  'stepper.aria': 'Bosqichlar',
  'step.anketa': 'Anketa',
  'step.myid': 'MyID',
  'step.scoring': 'Scoring',
  'step.results': 'Natijalar',
  'step.contract': 'Shartnoma',
  'step.done': 'Yakun',

  'anketa.title': 'Mijoz anketasi',
  'anketa.hint':
    "Qolgan ma'lumotlar (F.I.Sh, manzil) MyID identifikatsiyasidan keyin avtomatik to'ladi.",
  'anketa.identity.legend': "Shaxsni tasdiqlovchi ma'lumot",
  'anketa.identity.pinfl': 'JSHSHIR',
  'anketa.identity.passport': 'Pasport',
  'anketa.pinfl.label': 'JSHSHIR (14 raqam)',
  'anketa.passport.label': 'Pasport seriyasi va raqami',
  'anketa.birthDate': "Tug'ilgan sana",
  'anketa.card': 'Karta raqami',
  'anketa.expiry': 'Amal muddati',
  'anketa.panSuspect': "Karta raqamini tekshiring — xato terilganga o'xshaydi",
  'anketa.nonResident': 'Mijoz norezident',
  'anketa.submit': "Identifikatsiyaga o'tish →",
  'anketa.submitting': 'Yuborilmoqda…',
  'anketa.phoneNote': 'Davom etish uchun telefon raqamini tasdiqlang.',

  'phone.label': 'Telefon raqami',
  'phone.send': 'Kod yuborish',
  'phone.resend': 'Qayta yuborish',
  'phone.resendIn': 'Qayta yuborish ({seconds} s)',
  'phone.sendsLeft': 'Yana {n} marta yuborish mumkin.',
  'phone.verified': '✓ Tasdiqlandi',
  'phone.sent': "{phone} raqamiga kod yuborildi. Mijozdan so'rang.",
  'phone.mock': 'Mock rejim, kod: {code}',
  'phone.codeLabel': 'Tasdiqlash kodi',
  'phone.codePlaceholder': '6 raqamli kod',
  'phone.verify': 'Tasdiqlash',
  'phone.sendFail': 'Kod yuborilmadi',
  'phone.smsLang': 'SMS tili (mijoz uchun):',
  'phone.verifyFail': 'Kod tasdiqlanmadi',

  'contacts.legend': "Qo'shimcha kontaktlar",
  'contacts.hint': 'Kamida 2 ta kontakt, ulardan bittasi yaqin qarindosh bo\'lishi shart.',
  'contacts.add': '+ Yana kontakt',
  'contacts.type': '{n}-kontakt turi',
  'contacts.name': '{n}-kontakt ismi',
  'contacts.phone': '{n}-kontakt telefoni',
  'contacts.remove': '{n}-kontaktni olib tashlash',
  'contacts.namePlaceholder': 'Ism',
  'contacts.groupClose': 'Yaqin qarindosh',
  'contacts.groupOther': 'Boshqa',
  'contacts.needClose': "Kamida bitta yaqin qarindosh ko'rsating",
  'contacts.ownPhone': "Kontakt raqami mijozning o'z raqami bilan bir xil bo'lmasligi kerak",

  'note.parent': 'Ota / Ona',
  'note.spouse': 'Er / Xotin',
  'note.child': "O'g'il / Qiz",
  'note.siblings': 'Aka-uka / Opa-singil',
  'note.family': 'Qarindosh',
  'note.progenitor': 'Bobo / Buvi',
  'note.grandchild': 'Nabira',
  'note.friend': "Do'st",
  'note.colleague': 'Hamkasb',
  'note.additional': "Qo'shimcha",

  'myid.title': 'MyID identifikatsiyasi',
  'myid.hint':
    "Mijoz kameraga to'g'ri qarasin. Yuz to'liq kadrda bo'lishi, ko'zoynak va niqob bo'lmasligi kerak.",
  'myid.qrHint':
    "Mijoz o'z telefonida QR kodni ochsin va o'sha yerda suratga tushsin. Bu sahifa o'zi davom etadi.",
  'myid.qrAlt': 'Telefonda ochish uchun QR kod',
  'myid.qrStep1': 'Telefon kamerasini QR kodga tuting',
  'myid.qrStep2': "Ochilgan sahifada yuzni suratga oling",
  'myid.qrStep3': 'Sahifaga qayting — natija shu yerda chiqadi',
  'myid.qrWaiting': 'Telefondagi natija kutilmoqda…',
  'myid.useThisDevice': 'Shu qurilma kamerasi bilan',
  'myid.backToQr': 'QR kodga qaytish',

  'mobile.title': 'Yuzni suratga oling',
  'mobile.hint':
    "Telefonni ko'z darajasida ushlang, yuz ovalga to'liq sig'sin. Ko'zoynak va bosh kiyimni yeching.",
  'mobile.send': 'Yuborish',
  'mobile.startMyId': 'Identifikatsiyani boshlash →',
  'mobile.checking': 'Tekshirilmoqda…',
  'mobile.failedTitle': "Identifikatsiya o'tmadi",
  'mobile.incompleteTitle': "Yakunlab bo'lmadi",
  'mobile.failedHint': "Sotuvchidan yangi QR kod so'rang.",
  'mobile.doneTitle': 'Rasm qabul qilindi',
  'mobile.doneHint': "Telefonni yopib, sotuvchiga qayting — qolganini u davom ettiradi.",
  'mobile.errorTitle': "Ochib bo'lmadi",
  'mobile.expiredTitle': 'Havola muddati tugagan',
  'mobile.expiredHint': "Sotuvchidan yangi QR kod so'rang.",
  'myid.mockBadge': 'MOCK REJIM',
  'myid.shoot': 'Suratga olish',
  'myid.retake': '← Qayta olish',
  'myid.confirm': 'Tasdiqlash va davom etish →',
  'myid.checking': 'Tekshirilmoqda…',
  'myid.photoAlt': 'Olingan kadr',
  'myid.retry': "Qaytadan urinib ko'ring.",
  'myid.failedTitle': "Identifikatsiya o'tmadi",
  'myid.incompleteTitle': "Yakunlab bo'lmadi",
  'myid.startOver': 'Boshidan boshlash',
  'myid.err.denied': 'Kameraga ruxsat berilmadi. Brauzer sozlamalaridan ruxsat bering.',
  'myid.err.notFound': 'Kamera topilmadi.',
  'myid.err.generic': "Kamerani ishga tushirib bo'lmadi.",
  'myid.err.capture': "Kadrni olib bo'lmadi. Qaytadan urinib ko'ring.",
  'myid.mock.label': 'Xatolikni sinash:',
  'myid.mock.3': 'Tiriklik tekshiruvi',
  'myid.mock.21': "Yuz kadrga sig'madi",
  'myid.mock.22': 'Bir nechta odam',
  'myid.mock.2': "Pasport noto'g'ri",

  'client.verified': '✓ MyID orqali tasdiqlandi',
  'client.reused': '✓ MyID {date} da o\'tilgan — qayta so\'ralmadi',
  'client.similarity': "Yuz o'xshashlik darajasi",
  'client.birthDate': "Tug'ilgan sana",
  'client.pinfl': 'JSHSHIR',
  'client.passport': 'Pasport',
  'client.phone': 'Telefon',
  'client.card': 'Karta',
  'client.citizenship': 'Fuqarolik',
  'client.address': 'Manzil',

  'scoring.title': 'Parallel scoring',
  'scoring.hint':
    "Bitta tugma — Alif va Uzum'ga bir vaqtda so'rov yuboriladi. Natijalar yonma-yon ko'rsatiladi.",
  'scoring.start': 'Scoringni boshlash →',
  'scoring.waitTitle': "Ikkala provayderga so'rov yuborilmoqda",
  'scoring.waitHint': 'Iltimos, kuting. Bu bir daqiqagacha davom etishi mumkin.',

  'results.hint': "Mijoz o'ziga qulay provayderni tanlaydi.",
  'results.recommendOnly':
    "Rasmiylashtirish hozircha provayderning o'z ilovasida davom etadi — bu ekran taklif solishtirish uchun.",
  'results.timing': "Ikkala so'rov parallel yuborildi, jami {seconds} s.",
  'results.allFailedTitle': 'Afsuski, hech bir provayder tasdiqlamadi',
  'results.retry': "Scoringni qayta yuborish",
  'results.retryHint': "Provayder texnik sababdan javob bermadi.",
  'results.retrying': 'Yuborilmoqda…',
  'results.allFailedHint':
    "Sabablari quyida ko'rsatilgan. Mijozga boshqa to'lov usulini taklif qilish mumkin.",
  'result.limit': 'Tasdiqlangan limit',
  'result.duration': 'Muddat',
  'result.monthly': "Oylik to'lov",
  'result.markup': 'Ustama',
  'result.months': '{n} oy',
  'unit.seconds': '{n} s',
  'result.choose': 'Shu provayderni tanlash',
  'result.choosing': 'Tanlanmoqda…',
  'result.chosen': '✓ Tanlandi',
  'result.mock': 'namuna',

  // Scoring o'rtasida so'raladigan kod — kartani tasdiqlash uchun
  'result.otpTitle': "Provayder kod so'radi",
  'result.otpHint':
    "Kartani tasdiqlash uchun {phone} raqamiga kod yuborildi. Kod kiritilmaguncha limit hisoblanmaydi.",
  'result.otpHintNoPhone':
    "Kartani tasdiqlash uchun mijozning telefoniga kod yuborildi. Kod kiritilmaguncha limit hisoblanmaydi.",
  'result.otpConfirm': 'Tasdiqlash',
  'result.otpConfirming': 'Tekshirilmoqda…',
  'result.otpResend': 'Kodni qayta yuborish',
  'result.otpResending': 'Yuborilmoqda…',
  'result.otpResendsLeft': 'Yana {n} marta qayta yuborish mumkin',
  'result.otpAttempts': 'Qolgan urinishlar: {left}',
  'result.otpTimer': 'Kod amal qiladi: {time}',
  'result.otpMaybeExpired': "Kod muddati tugagan bo'lishi mumkin — yangi kod so'rang",
  'result.otpAskNew': "Alif talabi: noto'g'ri koddan keyin yangi kod so'rash kerak",
  'result.otpResendIn': "Yangi kodni {seconds} soniyadan keyin so'rash mumkin",
  'result.otpExpiredHint': "Kod o'ldi. Yangi kod so'rang — eskisini kiritish foydasiz",

  'status.PENDING': 'Kutilmoqda',
  'status.IN_PROGRESS': "So'rov yuborilmoqda",
  'status.AWAITING_OTP': 'Kod kutilmoqda',
  'history.title': "Do'konlar tarixi",
  'history.hint': "Qaysi mijoz, qachon va qanday limit olgan.",
  'history.total': 'Jami: {n}',
  'history.date': 'Sana',
  'history.client': 'Mijoz',
  'history.store': "Do'kon",
  'history.seller': 'Sotuvchi',
  'history.provider': 'Provayder',
  'history.status': 'Holat',
  'history.chosen': 'Tanlandi',
  'history.from': 'Boshlanishi',
  'history.to': 'Tugashi',
  'history.search': 'Qidirish',
  'history.searchPlaceholder': 'Ism yoki telefon',
  'history.reset': 'Filtrlarni tozalash',
  'history.allStores': "Barcha do'konlar",
  'history.allSellers': 'Barcha sotuvchilar',
  'history.allProviders': 'Ikkala provayder',
  'history.allStatuses': 'Barcha holatlar',
  'history.empty': "Bu shartlarga mos so'rov topilmadi.",
  'history.perPage': 'Sahifada',
  'history.pages': 'Sahifalar',
  'history.prev': '← Oldingi',
  'history.next': 'Keyingi →',
  'history.page': '{page} / {pages}',
  'request.PENDING': 'Navbatda',
  'request.IN_PROGRESS': 'Jarayonda',
  'request.CHOSEN': 'Tanlandi',
  'request.COMPLETED': 'Yakunlandi',
  'request.FAILED': 'Yakunlanmadi',
  'status.APPROVED': 'Tasdiqlandi',
  'status.REJECTED': 'Rad etildi',
  'status.TIMEOUT': 'Javob bermadi',
  'status.CHOSEN': 'Tanlandi',
  'status.NOT_CHOSEN': 'Tanlanmadi',
  'status.CONTRACT_ACTIVE': 'Shartnoma tuzildi',

  'flow.back': '← Anketaga qaytish',
  'flow.newClient': 'Yangi mijoz',
  'flow.restoring': 'Ish tiklanmoqda…',
  'contract.title': 'Shartnoma rasmiylashtirish',
  'contract.hint': 'Tovarlarni kiriting va muddatni tanlang. Summa provayder tasdiqlagan limitdan oshmasligi kerak.',
  'contract.items': 'Tovarlar',
  'contract.itemName': 'Tovar nomi',
  'contract.itemQty': 'Soni',
  'contract.itemPrice': "Narxi, so'm",
  'contract.itemSerial': 'Seriya raqami',
  'contract.addItem': '+ Yana tovar',
  'contract.removeItem': '{n}-tovarni olib tashlash',
  'contract.total': 'Jami',
  'contract.overLimit': 'Tasdiqlangan limitdan {amount} ga oshib ketdi',
  'contract.terms': 'Muddat va oylik to\'lov',
  'contract.monthly': 'oyiga {amount}',
  'contract.markup': 'ustama {percent}%',
  'contract.maxAmount': 'limit {amount}',
  'contract.submit': 'Arizani yuborish →',
  'contract.submitting': 'Yuborilmoqda…',
  'contract.otpTitle': 'Mijoz kodni tasdiqlasin',
  'contract.otpHint': '{provider} mijozning telefoniga kod yubordi. Mijozdan so\'rab, shu yerga kiriting.',
  'contract.otpAttempts': 'Qolgan urinishlar: {left}',
  'contract.confirm': 'Tasdiqlash va shartnoma tuzish',
  'contract.confirming': 'Tekshirilmoqda…',
  'contract.back': '← Tovarlarni tahrirlash',

  'done.title': 'Shartnoma tuzildi',
  'done.number': 'Shartnoma raqami',
  'done.provider': 'Provayder',
  'done.amount': 'Summa',
  'done.duration': 'Muddat',
  'done.pdfPending': 'Shartnoma va akt PDF ko\'rinishida — shablon provayderdan kelgach qo\'shiladi.',
  'done.newClient': 'Yangi mijoz',


  'date.placeholder': 'kk.oo.yyyy',
  'date.open': 'Kalendarni ochish',
  'date.prevMonth': 'Oldingi oy',
  'date.nextMonth': 'Keyingi oy',
  'date.month': 'Oy',
  'date.year': 'Yil',
  'date.clear': 'Tozalash',
  'date.invalid': "Sana noto'g'ri",
} as const;

export type MessageKey = keyof typeof uz;

const ru: Record<MessageKey, string> = {
  'app.product': 'Рассрочка',
  'app.name': 'ASKO broker',

  'lang.uz': 'Узбекский',
  'lang.ru': 'Русский',
  'lang.aria': 'Язык интерфейса',

  'common.cancel': 'Отмена',
  'common.close': 'Закрыть',
  'common.loading': 'Загрузка',

  'theme.aria': 'Тема',
  'theme.system': 'Как в системе',
  'theme.light': 'Светлая',
  'theme.dark': 'Тёмная',
  'nav.users': 'Пользователи',
  'users.alifReady': 'Отправляет (ID: {id})',
  'users.alifSystem': 'По общему ID',
  'users.alifNone': 'Не может отправлять в Alif',
  'flow.onBehalf': 'От чьего имени отправлять',
  'flow.onBehalfHint': 'Запрос в Alif уйдёт от имени этого продавца.',
  'flow.onBehalfNone': 'Нет продавцов, которые могут отправлять в Alif',
  'stores.title': 'Магазины',
  'stores.hint': 'Для нашего учёта: кто где работает. Провайдерам не отправляется.',
  'stores.name': 'Название магазина',
  'stores.namePlaceholder': 'Например: ASKO Чиланзар',
  'stores.add': 'Добавить',
  'stores.created': 'Магазин добавлен',
  'stores.sellers': 'продавцов: {n}',
  'event.details': 'Подробности',
  'event.hide': 'Свернуть',
  'event.identification.start': 'Идентификация начата',
  'event.identification.handoff_created': 'Создана QR-ссылка',
  'event.identification.success': 'Идентификация пройдена',
  'event.identification.failed': 'Идентификация не пройдена',
  'event.identification.reused': 'Повторный клиент — MyID не запрашивался',
  'event.identification.cancelled': 'Идентификация отменена',
  'event.scoring_request.start': 'Скоринг начат',
  'event.scoring_request.choose_provider': 'Выбран провайдер',
  'event.provider_result.scoring_approved': 'Провайдер одобрил',
  'event.provider_result.scoring_rejected': 'Провайдер отказал',
  'event.provider_result.scoring_error': 'Провайдер вернул ошибку',
  'event.provider_result.scoring_timeout': 'Провайдер не ответил',
  'event.provider_result.scoring_awaiting_otp': 'Провайдер запросил код',
  'event.provider_result.scoring_otp_confirmed': 'Код подтверждён',
  'event.provider_result.scoring_otp_failed': 'Код введён неверно',
  'event.provider_result.scoring_otp_resent': 'Код отправлен повторно',
  'event.otp.sent': 'Отправлен SMS-код',
  'event.otp.verified': 'Телефон подтверждён',
  'event.sales_user.login': 'Вход в систему',
  'event.sales_user.login_failed': 'Неудачная попытка входа',
  'event.sales_user.created': 'Пользователь создан',
  'event.sales_user.updated': 'Пользователь изменён',
  'event.sales_user.password_set': 'Пароль установлен',
  'event.sales_user.password_changed': 'Пароль изменён',
  'event.store.created': 'Магазин создан',
  'events.title': 'Что происходило',
  'events.empty': 'Событий нет',
  'events.userTitle': '{name} — последние действия',
  'events.clientTitle': '{name} — все обращения',
  'users.title': 'Пользователи',
  'users.hint': 'Продавцы, их магазин и номер в Alif.',
  'users.add': '+ Добавить пользователя',
  'users.create': 'Создать',
  'users.creating': 'Создаём…',
  'users.created': 'Пользователь создан',
  'users.name': 'Имя',
  'users.phone': 'Телефон',
  'users.password': 'Пароль',
  'users.passwordHint': 'Минимум 8 символов',
  'users.role': 'Роль',
  'users.store': 'Магазин',
  'users.noStore': 'Без магазина',
  'users.alifId': 'Alif ID',
  'users.alifIdHint': 'user-id в Alif',
  'users.myidToday': 'MyID сегодня',
  'users.lastLogin': 'Последний вход',
  'users.disabled': 'Отключён',
  'users.disable': 'Отключить',
  'users.enable': 'Включить',
  'users.disableTitle': 'Отключить пользователя',
  'users.disableBody': '{name} больше не сможет войти. Данные сохранятся.',
  'users.setPassword': 'Пароль',
  'users.setPasswordFor': 'Новый пароль для {name}',
  'users.newPassword': 'Новый пароль',
  'users.passwordSet': 'Пароль установлен',
  'users.save': 'Сохранить',
  'role.SALES': 'Продавец',
  'role.ADMIN': 'Администратор',
  'password.title': 'Смена пароля',
  'password.current': 'Текущий пароль',
  'password.next': 'Новый пароль',
  'password.repeat': 'Повторите новый пароль',
  'password.mismatch': 'Пароли не совпадают',
  'password.save': 'Сменить',
  'password.done': 'Пароль изменён',
  'nav.flow': 'Новая заявка',
  'nav.history': 'История',
  'header.logout': 'Выход',
  'logout.title': 'Выйти из системы?',
  'logout.body': 'Незаполненная анкета не сохранится.',
  'logout.confirm': 'Да, выйти',

  'login.subtitle': 'Кабинет продавца',
  'login.phone': 'Номер телефона',
  'login.password': 'Пароль',
  'login.submit': 'Войти',
  'login.submitting': 'Проверяем…',

  'stepper.aria': 'Этапы',
  'step.anketa': 'Анкета',
  'step.myid': 'MyID',
  'step.scoring': 'Скоринг',
  'step.results': 'Результаты',
  'step.contract': 'Договор',
  'step.done': 'Завершение',

  'anketa.title': 'Анкета клиента',
  'anketa.hint':
    'Остальные данные (Ф.И.О., адрес) заполнятся автоматически после идентификации в MyID.',
  'anketa.identity.legend': 'Документ, удостоверяющий личность',
  'anketa.identity.pinfl': 'ПИНФЛ',
  'anketa.identity.passport': 'Паспорт',
  'anketa.pinfl.label': 'ПИНФЛ (14 цифр)',
  'anketa.passport.label': 'Серия и номер паспорта',
  'anketa.birthDate': 'Дата рождения',
  'anketa.card': 'Номер карты',
  'anketa.expiry': 'Срок действия',
  'anketa.panSuspect': 'Проверьте номер карты — похоже на опечатку',
  'anketa.nonResident': 'Клиент нерезидент',
  'anketa.submit': 'Перейти к идентификации →',
  'anketa.submitting': 'Отправляем…',
  'anketa.phoneNote': 'Чтобы продолжить, подтвердите номер телефона.',

  'phone.label': 'Номер телефона',
  'phone.send': 'Отправить код',
  'phone.resend': 'Отправить снова',
  'phone.resendIn': 'Отправить снова ({seconds} с)',
  'phone.sendsLeft': 'Осталось отправок: {n}.',
  'phone.verified': '✓ Подтверждён',
  'phone.sent': 'Код отправлен на {phone}. Спросите его у клиента.',
  'phone.mock': 'Mock-режим, код: {code}',
  'phone.codeLabel': 'Код подтверждения',
  'phone.codePlaceholder': 'Код из 6 цифр',
  'phone.verify': 'Подтвердить',
  'phone.sendFail': 'Код не отправлен',
  'phone.smsLang': 'Язык SMS (для клиента):',
  'phone.verifyFail': 'Код не подтверждён',

  'contacts.legend': 'Дополнительные контакты',
  'contacts.hint': 'Минимум 2 контакта, один из них — близкий родственник.',
  'contacts.add': '+ Ещё контакт',
  'contacts.type': 'Тип контакта {n}',
  'contacts.name': 'Имя контакта {n}',
  'contacts.phone': 'Телефон контакта {n}',
  'contacts.remove': 'Удалить контакт {n}',
  'contacts.namePlaceholder': 'Имя',
  'contacts.groupClose': 'Близкий родственник',
  'contacts.groupOther': 'Прочее',
  'contacts.needClose': 'Укажите хотя бы одного близкого родственника',
  'contacts.ownPhone': 'Номер контакта не должен совпадать с номером клиента',

  'note.parent': 'Отец / Мать',
  'note.spouse': 'Муж / Жена',
  'note.child': 'Сын / Дочь',
  'note.siblings': 'Брат / Сестра',
  'note.family': 'Родственник',
  'note.progenitor': 'Дедушка / Бабушка',
  'note.grandchild': 'Внук / Внучка',
  'note.friend': 'Друг',
  'note.colleague': 'Коллега',
  'note.additional': 'Дополнительный',

  'myid.title': 'Идентификация MyID',
  'myid.hint':
    'Клиент должен смотреть прямо в камеру. Лицо целиком в кадре, без очков и маски.',
  'myid.qrHint':
    'Клиент открывает QR-код на своём телефоне и фотографируется там. Эта страница продолжит сама.',
  'myid.qrAlt': 'QR-код для открытия на телефоне',
  'myid.qrStep1': 'Наведите камеру телефона на QR-код',
  'myid.qrStep2': 'На открывшейся странице сделайте фото лица',
  'myid.qrStep3': 'Вернитесь к этому экрану — результат появится здесь',
  'myid.qrWaiting': 'Ждём результат с телефона…',
  'myid.useThisDevice': 'Камерой этого устройства',
  'myid.backToQr': 'Вернуться к QR-коду',

  'mobile.title': 'Сделайте фото лица',
  'mobile.hint':
    'Держите телефон на уровне глаз, лицо должно полностью помещаться в овал. Снимите очки и головной убор.',
  'mobile.send': 'Отправить',
  'mobile.startMyId': 'Начать идентификацию →',
  'mobile.checking': 'Проверяем…',
  'mobile.failedTitle': 'Идентификация не пройдена',
  'mobile.incompleteTitle': 'Не удалось завершить',
  'mobile.failedHint': 'Попросите у продавца новый QR-код.',
  'mobile.doneTitle': 'Фото принято',
  'mobile.doneHint': 'Закройте страницу и вернитесь к продавцу — дальше он продолжит.',
  'mobile.errorTitle': 'Не удалось открыть',
  'mobile.expiredTitle': 'Срок ссылки истёк',
  'mobile.expiredHint': 'Попросите у продавца новый QR-код.',
  'myid.mockBadge': 'MOCK-РЕЖИМ',
  'myid.shoot': 'Сделать снимок',
  'myid.retake': '← Переснять',
  'myid.confirm': 'Подтвердить и продолжить →',
  'myid.checking': 'Проверяем…',
  'myid.photoAlt': 'Сделанный снимок',
  'myid.retry': 'Попробуйте ещё раз.',
  'myid.failedTitle': 'Идентификация не пройдена',
  'myid.incompleteTitle': 'Не удалось завершить',
  'myid.startOver': 'Начать заново',
  'myid.err.denied': 'Доступ к камере запрещён. Разрешите его в настройках браузера.',
  'myid.err.notFound': 'Камера не найдена.',
  'myid.err.generic': 'Не удалось запустить камеру.',
  'myid.err.capture': 'Не удалось сделать снимок. Попробуйте ещё раз.',
  'myid.mock.label': 'Проверить ошибку:',
  'myid.mock.3': 'Проверка живости',
  'myid.mock.21': 'Лицо не поместилось',
  'myid.mock.22': 'Несколько человек',
  'myid.mock.2': 'Неверный паспорт',

  'client.verified': '✓ Подтверждён через MyID',
  'client.reused': '✓ MyID пройден {date} — повторно не запрашивали',
  'client.similarity': 'Степень схожести лица',
  'client.birthDate': 'Дата рождения',
  'client.pinfl': 'ПИНФЛ',
  'client.passport': 'Паспорт',
  'client.phone': 'Телефон',
  'client.card': 'Карта',
  'client.citizenship': 'Гражданство',
  'client.address': 'Адрес',

  'scoring.title': 'Параллельный скоринг',
  'scoring.hint':
    'Одна кнопка — запрос уходит в Alif и Uzum одновременно. Результаты покажем рядом.',
  'scoring.start': 'Запустить скоринг →',
  'scoring.waitTitle': 'Отправляем запрос обоим провайдерам',
  'scoring.waitHint': 'Подождите, это может занять до минуты.',

  'results.hint': 'Клиент выбирает удобного ему провайдера.',
  'results.recommendOnly':
    'Оформление пока продолжается в приложении самого провайдера — этот экран для сравнения предложений.',
  'results.timing': 'Оба запроса ушли параллельно, всего {seconds} с.',
  'results.allFailedTitle': 'К сожалению, ни один провайдер не одобрил',
  'results.retry': 'Отправить скоринг заново',
  'results.retryHint': 'Провайдер не ответил по технической причине.',
  'results.retrying': 'Отправляем…',
  'results.allFailedHint':
    'Причины указаны ниже. Клиенту можно предложить другой способ оплаты.',
  'result.limit': 'Одобренный лимит',
  'result.duration': 'Срок',
  'result.monthly': 'Ежемесячный платёж',
  'result.markup': 'Наценка',
  'result.months': '{n} мес.',
  'unit.seconds': '{n} с',
  'result.choose': 'Выбрать этого провайдера',
  'result.choosing': 'Выбираем…',
  'result.chosen': '✓ Выбран',
  'result.mock': 'тест',

  'result.otpTitle': 'Провайдер запросил код',
  'result.otpHint':
    'Для подтверждения карты код отправлен на {phone}. Пока код не введён, лимит не рассчитывается.',
  'result.otpHintNoPhone':
    'Для подтверждения карты код отправлен на телефон клиента. Пока код не введён, лимит не рассчитывается.',
  'result.otpConfirm': 'Подтвердить',
  'result.otpConfirming': 'Проверяем…',
  'result.otpResend': 'Отправить код повторно',
  'result.otpResending': 'Отправляем…',
  'result.otpResendsLeft': 'Повторно отправить можно ещё {n} раз(а)',
  'result.otpAttempts': 'Осталось попыток: {left}',
  'result.otpTimer': 'Код действует: {time}',
  'result.otpMaybeExpired': 'Срок кода мог истечь — запросите новый',
  'result.otpAskNew': 'Требование Alif: после неверного кода нужно запросить новый',
  'result.otpResendIn': 'Новый код можно запросить через {seconds} с',
  'result.otpExpiredHint': 'Код больше не действует. Запросите новый — старый вводить бесполезно',

  'status.PENDING': 'Ожидание',
  'status.IN_PROGRESS': 'Запрос отправляется',
  'status.AWAITING_OTP': 'Ожидает код',
  'history.title': 'История магазинов',
  'history.hint': 'Какой клиент, когда и какой лимит получил.',
  'history.total': 'Всего: {n}',
  'history.date': 'Дата',
  'history.client': 'Клиент',
  'history.store': 'Магазин',
  'history.seller': 'Продавец',
  'history.provider': 'Провайдер',
  'history.status': 'Статус',
  'history.chosen': 'Выбран',
  'history.from': 'С',
  'history.to': 'По',
  'history.search': 'Найти',
  'history.searchPlaceholder': 'Имя или телефон',
  'history.reset': 'Сбросить фильтры',
  'history.allStores': 'Все магазины',
  'history.allSellers': 'Все продавцы',
  'history.allProviders': 'Оба провайдера',
  'history.allStatuses': 'Все статусы',
  'history.empty': 'По этим условиям ничего не найдено.',
  'history.perPage': 'На странице',
  'history.pages': 'Страницы',
  'history.prev': '← Назад',
  'history.next': 'Вперёд →',
  'history.page': '{page} из {pages}',
  'request.PENDING': 'В очереди',
  'request.IN_PROGRESS': 'В процессе',
  'request.CHOSEN': 'Выбран',
  'request.COMPLETED': 'Завершён',
  'request.FAILED': 'Не завершён',
  'status.APPROVED': 'Одобрено',
  'status.REJECTED': 'Отказано',
  'status.TIMEOUT': 'Нет ответа',
  'status.CHOSEN': 'Выбран',
  'status.NOT_CHOSEN': 'Не выбран',
  'status.CONTRACT_ACTIVE': 'Договор заключён',

  'flow.back': '← Назад к анкете',
  'flow.newClient': 'Новый клиент',
  'flow.restoring': 'Восстанавливаем работу…',
  'contract.title': 'Оформление договора',
  'contract.hint': 'Введите товары и выберите срок. Сумма не должна превышать одобренный лимит.',
  'contract.items': 'Товары',
  'contract.itemName': 'Название товара',
  'contract.itemQty': 'Кол-во',
  'contract.itemPrice': 'Цена, сум',
  'contract.itemSerial': 'Серийный номер',
  'contract.addItem': '+ Ещё товар',
  'contract.removeItem': 'Удалить товар {n}',
  'contract.total': 'Итого',
  'contract.overLimit': 'Превышение одобренного лимита на {amount}',
  'contract.terms': 'Срок и ежемесячный платёж',
  'contract.monthly': '{amount} в месяц',
  'contract.markup': 'наценка {percent}%',
  'contract.maxAmount': 'лимит {amount}',
  'contract.submit': 'Отправить заявку →',
  'contract.submitting': 'Отправляем…',
  'contract.otpTitle': 'Клиент подтверждает код',
  'contract.otpHint': '{provider} отправил код на телефон клиента. Спросите его и введите здесь.',
  'contract.otpAttempts': 'Осталось попыток: {left}',
  'contract.confirm': 'Подтвердить и оформить договор',
  'contract.confirming': 'Проверяем…',
  'contract.back': '← Изменить товары',

  'done.title': 'Договор оформлен',
  'done.number': 'Номер договора',
  'done.provider': 'Провайдер',
  'done.amount': 'Сумма',
  'done.duration': 'Срок',
  'done.pdfPending': 'Договор и акт в PDF появятся, когда провайдер передаст шаблон.',
  'done.newClient': 'Новый клиент',


  'date.placeholder': 'дд.мм.гггг',
  'date.open': 'Открыть календарь',
  'date.prevMonth': 'Предыдущий месяц',
  'date.nextMonth': 'Следующий месяц',
  'date.month': 'Месяц',
  'date.year': 'Год',
  'date.clear': 'Очистить',
  'date.invalid': 'Неверная дата',
};

const DICTS: Record<Lang, Record<MessageKey, string>> = { uz, ru };

/** Kalendar uchun oy va hafta kunlari nomlari (dushanbadan boshlab). */
export const CALENDAR: Record<Lang, { months: string[]; weekdays: string[] }> = {
  uz: {
    months: [
      'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
      'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
    ],
    weekdays: ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya'],
  },
  ru: {
    months: [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
    ],
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  },
};

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Backend xatosini joriy tilda ko'rsatadi. */
  apiMessage: (error: ApiError | null | undefined) => string;
  /** MyID natija kodining matni joriy tilda. */
  myIdMessage: (code: number | null | undefined, fallback: string) => string;
  /**
   * Audit hodisasining nomi.
   *
   * Lug'atda bo'lmasa — kalitning o'zi qaytadi: yangi hodisa
   * qo'shilganda tasma jim qolib ketmasligi kerak.
   */
  eventLabel: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (LANGS as readonly string[]).includes(stored)) return stored as Lang;
  // Brauzer ruscha bo'lsa — ruscha, aks holda o'zbekcha.
  return navigator.language?.toLowerCase().startsWith('ru') ? 'ru' : DEFAULT_LANG;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // Til birinchi so'rovdan oldin ma'lum bo'lishi kerak, shuning uchun
  // `setApiLang` effekt emas, boshlang'ich qiymatda ham chaqiriladi.
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = readStoredLang();
    setApiLang(initial);
    return initial;
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    // Backend javoblari ham shu tilda kelsin (`Accept-Language`).
    setApiLang(lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const t: I18nContextValue['t'] = (key, vars) => interpolate(DICTS[lang][key], vars);

    return {
      lang,
      setLang,
      t,
      // Backend xabarni so'rov tilida qaytaradi. Lekin sotuvchi javob
      // kelgandan keyin tilni almashtirishi mumkin — shuning uchun kalit
      // (yoki xato kodi) bo'yicha qaytadan tarjima qilamiz.
      apiMessage: (error) => {
        if (!error) return '';
        if (error.messageKey) {
          return translate(error.messageKey, lang, error.messageParams);
        }
        return error.message || errorMessage(error.code, lang);
      },
      myIdMessage: (code, fallback) => myIdFailureMessage(code, fallback, lang),
      eventLabel: (key) => {
        const dictionary = lang === 'ru' ? ru : uz;
        return (dictionary as Record<string, string>)[`event.${key}`] ?? key;
      },
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n faqat I18nProvider ichida ishlatiladi');
  return ctx;
}
